import { Fragment } from 'react'

import { Box, Button, Group, ScrollArea, Table, Text } from '@mantine/core'

import { IOrg } from '#state/types'

import { InviteTableRow } from './InviteTableRow'
import { MemberTableRow } from './MemberTableRow'

import styles from './MemberTable.module.scss'

export type EmptySeatRows = {
  available: number
  leaving: number
  leavingLabel?: string
  busy?: boolean
  onInvite?: () => void
  onRemove?: () => void
  onKeep?: () => void
}

export const MemberTable = ({
  workspace,
  emptySeats,
}: {
  workspace: IOrg
  emptySeats?: EmptySeatRows
}) => {
  const availableSeatRows = emptySeats
    ? Array.from(
        { length: emptySeats.available - emptySeats.leaving },
        (_unused, index) => (
          <EmptySeatRow
            key={`available-${index}`}
            label="Available seat"
            busy={emptySeats.busy}
            actions={[
              ['Invite New Member', emptySeats.onInvite],
              ['Remove', emptySeats.onRemove],
            ]}
          />
        ),
      )
    : null

  const leavingSeatRows = emptySeats
    ? Array.from({ length: emptySeats.leaving }, (_unused, index) => (
        <EmptySeatRow
          key={`leaving-${index}`}
          label={`Scheduled to be removed ${emptySeats.leavingLabel ? `on ${emptySeats.leavingLabel}` : 'at renewal'}`}
          leaving
          busy={emptySeats.busy}
          actions={[['Unschedule Removal', emptySeats.onKeep]]}
        />
      ))
    : null

  return (
    <ScrollArea className={styles.membertablescroll} scrollbars="y">
      <Table
        verticalSpacing="sm"
        classNames={{
          th: styles.membertable_th,
          tbody: styles.membertable,
        }}
        key={`workspace-table-${workspace.owner?.id}`} // to ensure we recreate the table if/when the owner changes
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {workspace.openInvites.map((i) => (
            <Fragment key={i.id}>
              <InviteTableRow invite={i} workspace={workspace} />
            </Fragment>
          ))}
          {workspace.alphabetizedMembers.map((m) => (
            <Fragment key={m.id}>
              <MemberTableRow member={m} />
            </Fragment>
          ))}
          {availableSeatRows}
          {leavingSeatRows}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  )
}

const EmptySeatRow = ({
  label,
  leaving,
  busy,
  actions,
}: {
  label: string
  leaving?: boolean
  busy?: boolean
  actions: [string, (() => void) | undefined][]
}) => (
  <Table.Tr bd="1px dashed var(--mantine-color-gray-4)">
    <Table.Td>
      <Group gap="sm">
        <Box
          w={40}
          h={40}
          bdrs="50%"
          bd={`2px ${leaving ? 'solid' : 'dashed'} var(--mantine-color-gray-4)`}
        />
        <Text fz={14} fs="italic" c="dimmed">
          {label}
        </Text>
      </Group>
    </Table.Td>
    <Table.Td></Table.Td>
    <Table.Td ta="right">
      <Group gap={0} justify="flex-end">
        {actions.map(([text, onClick]) =>
          onClick ? (
            <Button
              key={text}
              fz={15}
              mr={5}
              variant="subtle"
              color={text === 'Remove' ? 'red' : undefined}
              loading={busy && text !== 'Invite New Member'}
              disabled={busy}
              onClick={onClick}
            >
              {text}
            </Button>
          ) : null,
        )}
      </Group>
    </Table.Td>
  </Table.Tr>
)
